import { loadTheme, IPartialTheme, ITheme, ThemeProvider } from '@fluentui/react';
import { FluentProvider, webLightTheme, webDarkTheme, teamsDarkV21Theme, teamsLightV21Theme } from '@fluentui/react-components';
import { useMemo, useEffect } from 'react';
import { DefaultTheme } from '../../../Theme/DefaultTheme';
import { TeamsDarkTheme } from '../../../Theme/TeamsDarkTheme';
import { TeamsTheme } from '../../../Theme/TeamsTheme';
import { useUiContext } from '../UIContext/uiCtx';

export const TimeThemeProvider = ({ children }: { children?: React.ReactNode }) => {
    const { isDarkmode, isTeams, isInModelDrivenApp } = useUiContext();
    
    const theme = useMemo(() => {
        // For model-driven apps, prioritize parent's dark mode detection
        if (isInModelDrivenApp) {
            return isDarkmode ? TeamsDarkTheme : DefaultTheme;
        }
        // Teams context
        if (isTeams) {
            return isDarkmode ? TeamsDarkTheme : TeamsTheme;
        }
        // Regular app context
        return isDarkmode ? TeamsDarkTheme : DefaultTheme;
    }, [isDarkmode, isTeams, isInModelDrivenApp]);

    const themeV9 = useMemo(() => {
        if (isTeams) {
            if (isDarkmode) {
                return teamsDarkV21Theme;
            }
            return teamsLightV21Theme;
        }
        if (isDarkmode) {
            return webDarkTheme;
        }
        return webLightTheme;
    }, [isDarkmode, isTeams]);

    useEffect(() => {
        loadTheme(theme);
        // hotfix/polyfil for theme as css variables
        const applyThemeCssVariables = (theme: IPartialTheme) => {
            const themeprovider = document.getElementById('themeprovider');
            const apply = (theme: IPartialTheme, keyName: keyof ITheme) => {
                Object.entries(theme[keyName]).forEach(([key, value]: [string, string]) => {
                    themeprovider.style.setProperty(`--${keyName}-${key}`, value);
                });
            };
            apply(theme, 'palette');
            apply(theme, 'semanticColors');
            apply(theme, 'effects');
            apply(theme, 'spacing');
        };
        applyThemeCssVariables(theme);
    }, [isDarkmode, isTeams, theme]);

    return (
        <FluentProvider theme={themeV9}>
            <ThemeProvider id="themeprovider" theme={theme} className={`root${isDarkmode ? ' isDarkmode' : ''}`}>
                {children}
            </ThemeProvider>
        </FluentProvider>
    );
};
